import React, { useState, useRef, useEffect } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import "../../styles/Admin.css";
import AdminFooter from "../common/AdminFooter";
import AdminSideBar from "./AdminSideBar";
import getUserMenuItems from "./AdminMenuContent";
import { api, adminReqConfig, baseURL } from "../../include/api";
import { useNotification } from "../../Toast/ToastProvider";
import Spinner from "../add-ons/Spinner";
import "../../styles/CategoryProductList.css";
import { ERROR, SUCCESS } from "../../include/constant";
import { useNavigate } from "react-router-dom";

const AddBrand = () => {
  const homeLink = "/admin";
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState("");
  const [status, setStatus] = useState(true);
  const [categoryList, setCategoryList] = useState([]);
  const [fetchingCategory, setFetchingCategory] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const mainImageElement = useRef();
  const toast = useNotification();
  const urlParams = new URLSearchParams(window.location.search);
  const brandIdFromUrl = urlParams.get("id");
  const isViewMode = urlParams.get("view") === "true";
  const isEditMode = !!brandIdFromUrl && !isViewMode;
  const [brandId, setBrandId] = useState(
    brandIdFromUrl ? parseInt(brandIdFromUrl, 10) : null,
  );

  const inputStyle = {
    borderRadius: "5px",
    fontWeight: "400",
    outline: "none",
    width: "100%",
    padding: "11px 24px",
    backgroundColor: "transparent !important",
    border: "1px solid var(--gray-100)",
    color: "hsl(var(--black))",
  };

  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();

  const fetchBrandDetails = (id) => {
    api
      .get(`/brand/?id=${id}`, adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          const brand = Array.isArray(res.data.data)
            ? res.data.data[0]
            : res.data.data;
          if (!brand) return;
          setBrandName(brand.name || "");
          setCategoryId(brand.categoryId?.toString() || "");
          setDescription(brand.description || "");
          setStatus(brand.status === "active");
          if (brand.imageUrl) {
            setMainImagePreview(baseURL + "/" + brand.imageUrl);
            setImageUrl(brand.imageUrl || "");
          }
        }
      })
      .catch(() => {
        toast({ type: "error", message: "Failed to load brand details." });
      });
  };

  const fetchCategories = () => {
    setFetchingCategory(true);
    api
      .get("/category/?flag=exceptHome", adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          setCategoryList(res.data.data || []);
        }
      })
      .catch(() => setCategoryList([]))
      .finally(() => setFetchingCategory(false));
  };

  useEffect(() => {
    fetchCategories();
    if (brandId) {
      fetchBrandDetails(brandId);
    }
  }, [brandId]);

  const handleMainImage = (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (
      !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
        file.type,
      )
    ) {
      toast({ type: "error", message: "Only JPG, PNG, WebP allowed" });
      mainImageElement.current.value = "";
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast({ type: "error", message: "Image must be under 3MB" });
      mainImageElement.current.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setMainImagePreview(reader.result);
      setImageUrl(file);
    };
    reader.readAsDataURL(file);
    mainImageElement.current.value = "";
  };

  const validate = () => {
    if (!brandName.trim()) {
      toast({ type: "error", message: "Brand name is required" });
      return false;
    }
    if (!categoryId) {
      toast({ type: "error", message: "Please select a category" });
      return false;
    }
    return true;
  };

  const handleSaveMainImage = () => {
    return new Promise((resolve, reject) => {
      if (!(imageUrl instanceof File)) return resolve(null);
      const data = new FormData();
      data.append("imageUrl", imageUrl);
      api
        .post(`/brand/?id=${brandId}&flag=changeimage`, data, adminReqConfig())
        .then((response) => {
          if (response.status === 200) {
            toast({ type: SUCCESS, message: response.data.message });
            resolve(response.data);
          } else {
            reject(new Error("Unexpected response status"));
          }
        })
        .catch((error) => {
          toast({
            type: ERROR,
            message: error.response?.data?.message || error.message,
          });
          reject(error);
        });
    });
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      if (isEditMode && imageUrl instanceof File) {
        await handleSaveMainImage();
      }

      let savePromise;
      if (!isEditMode) {
        const formData = new FormData();
        formData.append("name", brandName.trim());
        formData.append("categoryId", categoryId);
        formData.append("description", description.trim());
        formData.append("status", status ? "active" : "inactive");
        if (imageUrl instanceof File) {
          formData.append("imageUrl", imageUrl);
        }
        savePromise = api.post("/brand/", formData, {
          ...adminReqConfig(),
          headers: { ...adminReqConfig().headers, "Content-Type": undefined },
        });
      } else {
        const payload = {
          name: brandName.trim(),
          categoryId: Number(categoryId),
          description: description.trim(),
          status: status ? "active" : "inactive",
        };
        savePromise = api.put(
          `/brand/?id=${brandId}`,
          payload,
          adminReqConfig(),
        );
      }

      const res = await savePromise;
      if (![200, 201].includes(res.status)) throw new Error("Save failed");

      toast({
        type: "success",
        message: isEditMode
          ? "Brand updated successfully!"
          : "Brand added successfully!",
      });
      navigate("/admin/brand/brandList");
      if (!isEditMode) clearFields();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (isEditMode ? "Failed to update brand" : "Failed to add brand");
      toast({ type: "error", message: msg });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const clearFields = () => {
    if (saving) return;
    setBrandName("");
    setCategoryId("");
    setDescription("");
    setImageUrl(null);
    setMainImagePreview("");
    setStatus(true);
  };

  const parentCategories = categoryList.filter(
    (cat) => String(cat.parentId) !== "-1",
  );

  const getButtonStyle = (isPrimary = false) => ({
    border: "none",
    padding: "8px 20px",
    fontWeight: "500",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    ...(isPrimary
      ? {
          backgroundColor: saving ? "#1565c0" : "#1976d2",
          color: "#fff",
        }
      : {
          backgroundColor: saving ? "#e0e0e0" : "#bed9f8ff",
          color: saving ? "#9e9e9e" : "#1976d2",
        }),
  });

  const renderLoadingSpinner = (size = "sm") => (
    <i
      className={`fa fa-spinner fa-spin ${size === "sm" ? "me-2" : "me-3"}`}
      aria-hidden="true"
      style={{ fontSize: size === "sm" ? "0.9rem" : "1.1rem" }}
    ></i>
  );

  return (
    <>
      {isViewMode ? (
        <>
          <AdminSideBar
            menuContent={menuContent}
            isMinimized={isSidebarMinimized}
          />
          <AdminTopNavBar
            toggleSidebar={toggleSidebar}
            isSidebarMinimized={isSidebarMinimized}
            homeLink={homeLink}
            title="BRAND DETAILS"
          />
          <div
            className="content-wrapper p-0"
            style={{
              marginLeft: isSidebarMinimized ? "80px" : "250px",
              flex: "1",
            }}
          >
            <section
              style={{
                backgroundColor: "#F1F4F9",
                padding: "30px",
                marginTop: "75px",
                marginBottom: "50px",
                height: "100vh",
              }}
            >
              {fetchingCategory ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "50vh" }}
                >
                  <Spinner color="primary" />
                </div>
              ) : (
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-lg-3 mb-4">
                      <div
                        className="card p-8"
                        style={{ height: "300px", border: "none" }}
                      >
                        <div className="card-body d-flex flex-column align-items-start">
                          <div className="mb-3" style={{ textAlign: "center" }}>
                            {mainImagePreview ? (
                              <img
                                src={mainImagePreview}
                                alt="Brand"
                                className="img-fluid rounded"
                                style={{
                                  width: "190px",
                                  height: "190px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <div
                                className="bg-light d-flex justify-content-center align-items-center"
                                style={{
                                  width: "190px",
                                  height: "190px",
                                  borderRadius: "8px",
                                }}
                              >
                                <i
                                  className="fa fa-image text-muted"
                                  style={{ fontSize: "48px" }}
                                ></i>
                              </div>
                            )}
                          </div>
                          <h6
                            className="card-title fs-5 mt-16"
                            style={{ textAlign: "left", width: "100%" }}
                          >
                            {brandName || "Brand Name"}
                          </h6>
                          <p
                            className="card-text text-muted mb-1"
                            style={{ textAlign: "left", width: "100%" }}
                          >
                            <strong>Category:</strong>{" "}
                            {parentCategories.find(
                              (c) => c.id === Number(categoryId),
                            )?.name || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-9">
                      <div className="card mb-22 pb-40 border-0">
                        <div className="d-flex justify-content-between align-items-center p-16">
                          <h6 className="mb-0 fs-5">Brand Description</h6>
                        </div>
                        <div className="card-body" style={{ height: "200px" }}>
                          <div className="row px-16">
                            <textarea
                              className="form-control border-0"
                              rows="8"
                              value={description}
                              readOnly
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
          <AdminFooter isSidebarMinimized={isSidebarMinimized} />
        </>
      ) : (
        <>
          <AdminSideBar
            menuContent={menuContent}
            isMinimized={isSidebarMinimized}
          />
          <AdminTopNavBar
            toggleSidebar={toggleSidebar}
            isSidebarMinimized={isSidebarMinimized}
            homeLink={homeLink}
            title={isEditMode ? "EDIT BRAND" : "ADD NEW BRAND"}
          />
          <div
            className="content-wrapper p-0"
            style={{
              marginLeft: isSidebarMinimized ? "80px" : "250px",
              flex: "1",
            }}
          >
            <section
              style={{
                backgroundColor: "#F1F4F9",
                padding: "30px",
                marginTop: "70px",
                marginBottom: "40px",
              }}
            >
              {fetchingCategory ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "50vh" }}
                >
                  <Spinner color="primary" />
                </div>
              ) : (
                <div className="container-fluid">
                  <div className="row">
                    {/* Preview */}
                    <div className="col-lg-3 mb-4">
                      <div
                        className="card p-8"
                        style={{ height: "400px", border: "none" }}
                      >
                        <div className="card-body d-flex flex-column align-items-start">
                          <div className="mb-3" style={{ textAlign: "center" }}>
                            {mainImagePreview ? (
                              <img
                                src={mainImagePreview}
                                alt="Brand"
                                className="img-fluid rounded"
                                style={{
                                  width: "190px",
                                  height: "190px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <div
                                className="bg-light d-flex justify-content-center align-items-center"
                                style={{
                                  width: "190px",
                                  height: "190px",
                                  borderRadius: "8px",
                                }}
                              >
                                <i
                                  className="fa fa-image text-muted"
                                  style={{ fontSize: "48px" }}
                                ></i>
                              </div>
                            )}
                          </div>
                          <h6
                            className="card-title fs-5 mt-16"
                            style={{ textAlign: "left", width: "100%" }}
                          >
                            {brandName || "Brand Name"}
                          </h6>
                          <p
                            className="card-text text-muted mb-1"
                            style={{ textAlign: "left", width: "100%" }}
                          >
                            <strong>Category:</strong>{" "}
                            {parentCategories.find(
                              (c) => c.id === Number(categoryId),
                            )?.name || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Form */}
                    <div className="col-lg-9">
                      <div className="card mb-22 pb-40 border-0">
                        <div className="d-flex justify-content-between align-items-center p-16">
                          <h6 className="mb-0 fs-5">
                            {isEditMode
                              ? "Edit Brand Image"
                              : "Add Brand Image"}
                          </h6>
                          <div className="d-flex align-items-center gap-2">
                            <span className="pe-10">Status</span>
                            <div className="form-check form-switch mb-0">
                              <input
                                className={`form-check-input form-product-status-switch ${
                                  status === true
                                    ? "switch-green"
                                    : "switch-orange"
                                }`}
                                type="checkbox"
                                id="statusSwitch"
                                checked={status}
                                disabled={saving}
                                onChange={(e) =>
                                  !saving && setStatus(e.target.checked)
                                }
                              />
                            </div>
                          </div>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                          <div
                            className="border-dashed text-center cursor-pointer"
                            onClick={() =>
                              !saving && mainImageElement.current?.click()
                            }
                            style={{
                              border: "2px dashed #ccc",
                              borderRadius: "8px",
                              transition: "border-color 0.2s",
                              minHeight: "270px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              opacity: saving ? 0.6 : 1,
                              cursor: saving ? "not-allowed" : "pointer",
                            }}
                            onMouseEnter={(e) => {
                              if (!saving)
                                e.currentTarget.style.borderColor = "#0d6efd";
                            }}
                            onMouseLeave={(e) => {
                              if (!saving)
                                e.currentTarget.style.borderColor = "#ccc";
                            }}
                          >
                            {mainImagePreview ? (
                              <div className="d-flex flex-column align-items-center w-100">
                                <img
                                  src={mainImagePreview}
                                  alt="Main"
                                  className="img-fluid mb-2"
                                  style={{
                                    maxHeight: "180px",
                                    objectFit: "contain",
                                  }}
                                />
                                <small className="text-muted">
                                  {saving
                                    ? "Saving image..."
                                    : "Current brand image"}
                                </small>
                              </div>
                            ) : (
                              <>
                                <i
                                  className="fa fa-cloud-upload-alt mb-3"
                                  style={{
                                    fontSize: "48px",
                                    color: saving ? "#9e9e9e" : "#0d6efd",
                                  }}
                                ></i>
                                <h6>
                                  {saving ? (
                                    "Processing..."
                                  ) : (
                                    <>
                                      Drop Your Images Here, Or{" "}
                                      <span className="text-primary">
                                        Click To Browse
                                      </span>
                                    </>
                                  )}
                                </h6>
                                <small className="text-muted">
                                  {saving
                                    ? "Please wait while saving..."
                                    : "Square Pixels Recommended. PNG, JPG, WebP allowed."}
                                </small>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            onChange={(e) =>
                              !saving && handleMainImage(e.target.files)
                            }
                            className="d-none"
                            accept="image/*"
                            ref={mainImageElement}
                            disabled={saving}
                          />
                        </div>
                      </div>

                      <div className="card border-0">
                        <div className="d-flex justify-content-between align-items-center p-16">
                          <h6 className="mb-0 fs-5">Brand Information</h6>
                          <button
                            onClick={clearFields}
                            disabled={saving}
                            style={getButtonStyle(false)}
                          >
                            {saving ? (
                              <>
                                {renderLoadingSpinner("sm")}
                                Clearing...
                              </>
                            ) : (
                              "Clear"
                            )}
                          </button>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6 mb-16">
                              <h6 className="text-md mb-16 fw-bold">
                                Brand Name
                              </h6>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter Brand Name"
                                value={brandName}
                                onChange={(e) =>
                                  !saving && setBrandName(e.target.value)
                                }
                                disabled={saving}
                                style={inputStyle}
                              />
                            </div>
                            <div className="col-md-6 mb-16">
                              <h6 className="text-md mb-16 fw-bold">
                                Category
                              </h6>
                              <select
                                className="form-select"
                                value={categoryId}
                                onChange={(e) =>
                                  !saving && setCategoryId(e.target.value)
                                }
                                disabled={saving}
                                style={inputStyle}
                              >
                                <option value="">Select Category</option>
                                {parentCategories.map((cat) => (
                                  <option key={cat.id} value={cat.id}>
                                    {cat.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="mb-16">
                            <h6 className="text-md mb-16 fw-bold">
                              Brand Description
                            </h6>
                            <textarea
                              className="form-control"
                              rows="4"
                              placeholder="Your Description Here..."
                              value={description}
                              onChange={(e) =>
                                !saving && setDescription(e.target.value)
                              }
                              disabled={saving}
                            ></textarea>
                          </div>
                          <div className="d-flex justify-content-end gap-8">
                            <button
                              onClick={handleSave}
                              disabled={saving}
                              style={getButtonStyle(true)}
                            >
                              {saving ? (
                                <>
                                  {renderLoadingSpinner("sm")}
                                  {isEditMode ? "Updating..." : "Adding..."}
                                </>
                              ) : isEditMode ? (
                                "Update"
                              ) : (
                                "Add"
                              )}
                            </button>
                            <button
                              onClick={clearFields}
                              disabled={saving}
                              style={getButtonStyle(false)}
                            >
                              {saving ? (
                                <>
                                  {renderLoadingSpinner("sm")}
                                  Clearing...
                                </>
                              ) : (
                                "Clear All"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
          <AdminFooter isSidebarMinimized={isSidebarMinimized} />
        </>
      )}
    </>
  );
};

export default AddBrand;
